import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { motion } from 'framer-motion';
import { useNavigate, NavLink } from 'react-router-dom';
import {
  EditableContent,
  getModelEntryData,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
  usePublishedNews,
} from '@lazylabs-dev/editry-react';
import bikedealer from '../images/nukeproof-dealers.png';
import nukeproofImage from '../images/nukeproof.png';
import InstagramPosts from '../components/InstagramPosts';
import BikeCategory from '../components/BikeCategory';
import NovinkyItem from '../components/NovinkyItem';
import { mockBikeCategories, mockNewsArticles } from '../utils/mockData';
import { getLocalizedText } from '../utils/textLocalization';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const getContentValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return fallback;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return fallback;
  }

  let record = null;
  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      break;
    }
  }

  if (!record) {
    record = records[0];
  }

  return typeof record?.value === 'string' && record.value.trim() !== '' ? record.value : fallback;
};

const toPlainText = (html = '') => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

function HomeHeroHeader({ allContent, preferredLanguages, isEditModeActive, editryLanguage }) {
  const videoUrl = getContentValue(allContent, 'header_header_video', '', preferredLanguages);
  const imgUrl = getContentValue(allContent, 'header_header_obrazek', '', preferredLanguages);
  const isVideo = getContentValue(allContent, 'header_header_zapnutoVideo', false, preferredLanguages);

  const renderEditableOrText = (contentKey, fallback) => {
    if (isEditModeActive) {
      return (
        <EditableContent
          contentKey={contentKey}
          language={editryLanguage}
          as="span"
          fallback={getContentValue(allContent, contentKey, fallback, preferredLanguages)}
          showFallbackWhileLoading={false}
        />
      );
    }

    return (
      <span>{getContentValue(allContent, contentKey, fallback, preferredLanguages)}</span>
    );
  };

  return (
    <motion.div
      key="header"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div id="wrapper">
        <header
          id="banner"
          className="scrollto clearfix"
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isVideo === "true" ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 1,
              }}
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <>
              <img src={imgUrl} alt="Header" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 1,
              }} />
            </>
          )}

          <div id="banner-content" className="row clearfix" style={{ position: 'relative', zIndex: 2 }}>
            <div className="col-38">
              <hr className="section-heading-header-hr" />
              <div className="section-heading-header">
                <h1 className="barlow-extrabold shadow" id="hlavni-nadpis">
                  {renderEditableOrText('header_header_nazev', 'Nukeproof')}
                </h1>
                <h2 id="hlavni-popis" className="barlow-medium shadow">
                  {renderEditableOrText('header_header_popis', 'Precision bikes for every trail.')}
                </h2>
              </div>
              <a href="#kola" className="pointer weird-button barlow-semibold animated scrollto clearfix">
                {renderEditableOrText('header_header_tlacitko', 'Explore bikes')}
              </a>
            </div>
          </div>
        </header>
      </div>
    </motion.div>
  );
}

function HomeEditry({ kolaRef, scrollTo }) {
  const navigate = useNavigate();
  const localKolaRef = useRef(null);
  const sliderRef = kolaRef || localKolaRef;

  const dealersTitleText = getLocalizedText('home.dealers_title');
  const dealersSubtitleText = getLocalizedText('home.dealers_subtitle');
  const dealersButtonText = getLocalizedText('home.dealers_button');
  const newsTitleText = getLocalizedText('home.news_title');
  const moreArticlesText = getLocalizedText('home.more_articles');

  const editryReady = isEditryConfigured();
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const { overlayEnabled, adminSession, client } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);
  usePreloadPublicData({ includeNews: true, auto: editryReady });
  const [pick4Categories, setPick4Categories] = useState([]);

  const contentListResponse = usePublicContentList();
  const publishedNewsResponse = usePublishedNews();

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  useEffect(() => {
    document.title = getLocalizedText('home.page_title');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPick4 = async () => {
      if (!editryReady) {
        setPick4Categories([]);
        return;
      }

      try {
        const modelsResponse = await client.getPublicModels();
        const pick4Model = modelsResponse?.data?.find((model) => model.slug === 'pick-4');

        if (!pick4Model) {
          if (!cancelled) {
            setPick4Categories([]);
          }
          return;
        }

        let itemsResponse = await client.getPublicModelItems(pick4Model.id, editryLanguage);
        let items = itemsResponse?.data || [];

        if (!items.length) {
          itemsResponse = await client.getPublicModelItems(pick4Model.id);
          items = itemsResponse?.data || [];
        }

        const parsed = items
          .map((item) => {
            const fields = getModelEntryData(item);
            return {
              data: {
                cesta: fields.cesta || item.slug || '',
                nazev: fields.nazev || item.name || '',
                popis: fields.popis || '',
                obrazek: fields.obrazek || nukeproofImage,
              },
            };
          })
          .filter((item) => item.data.cesta && item.data.nazev);

        if (!cancelled) {
          setPick4Categories(parsed);
        }
      } catch (error) {
        if (!cancelled) {
          setPick4Categories([]);
        }
      }
    };

    loadPick4();

    return () => {
      cancelled = true;
    };
  }, [client, editryLanguage, editryReady]);

  const categoryData = useMemo(() => {
    if (!editryReady) {
      return mockBikeCategories;
    }

    return pick4Categories;
  }, [editryReady, pick4Categories]);

  
  const sliderDragRef = useRef({
    isDragging: false,
    dragMoved: false,
    ignoreClick: false,
    startX: 0,
    startScrollLeft: 0,
    lastDeltaX: 0,
    animationFrameId: null,
  });
  const sliderHoverRef = useRef(false);
  const sliderAutoplayRef = useRef(null);

  const stopSliderAutoplay = () => {
    if (sliderAutoplayRef.current !== null) {
      window.clearInterval(sliderAutoplayRef.current);
      sliderAutoplayRef.current = null;
    }
  };

  useEffect(() => {
    const slider = sliderRef?.current;
    const dragRef = sliderDragRef.current;

    if (!slider) {
      return undefined;
    }

    const onMouseMove = (event) => {
      if (!sliderDragRef.current.isDragging) {
        return;
      }

      const deltaX = event.clientX - sliderDragRef.current.startX;
      if (Math.abs(deltaX) > 5) {
        sliderDragRef.current.dragMoved = true;
        sliderDragRef.current.ignoreClick = true;
      }

      sliderDragRef.current.lastDeltaX = deltaX;

      if (sliderDragRef.current.animationFrameId === null) {
        sliderDragRef.current.animationFrameId = window.requestAnimationFrame(() => {
          slider.scrollLeft = sliderDragRef.current.startScrollLeft - sliderDragRef.current.lastDeltaX * 0.94;
          sliderDragRef.current.animationFrameId = null;
        });
      }

      event.preventDefault();
    };

    const onMouseUp = () => {
      if (!sliderDragRef.current.isDragging) {
        return;
      }

      sliderDragRef.current.isDragging = false;
      slider.classList.remove('is-grabbing');

      if (sliderDragRef.current.animationFrameId !== null) {
        window.cancelAnimationFrame(sliderDragRef.current.animationFrameId);
        sliderDragRef.current.animationFrameId = null;
      }

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    const onMouseEnter = () => {
      sliderHoverRef.current = true;
    };

    const onMouseLeave = () => {
      sliderHoverRef.current = false;
    };

    const startSliderAutoplay = () => {
      if (sliderAutoplayRef.current !== null) {
        return;
      }

      sliderAutoplayRef.current = window.setInterval(() => {
        if (sliderHoverRef.current || sliderDragRef.current.isDragging) {
          return;
        }

        const firstCard = slider.querySelector('.option2');
        const cardWidth = firstCard?.getBoundingClientRect().width || slider.clientWidth;

        if (!cardWidth || slider.scrollWidth <= slider.clientWidth) {
          return;
        }

        const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
        const currentScrollLeft = slider.scrollLeft;
        const nextScrollLeft = Math.min(currentScrollLeft + cardWidth, maxScrollLeft);

        if (currentScrollLeft >= maxScrollLeft - 2) {
          slider.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        slider.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
      }, 5000);
    };

    const onMouseDown = (event) => {
      if (event.button !== 0) {
        return;
      }

      sliderDragRef.current.isDragging = true;
      sliderDragRef.current.dragMoved = false;
      sliderDragRef.current.startX = event.clientX;
      sliderDragRef.current.startScrollLeft = slider.scrollLeft;

      slider.classList.add('is-grabbing');
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    const onClickCapture = (event) => {
      if (!sliderDragRef.current.ignoreClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      sliderDragRef.current.ignoreClick = false;
    };

    slider.addEventListener('mousedown', onMouseDown);
    slider.addEventListener('click', onClickCapture, true);
    slider.addEventListener('mouseenter', onMouseEnter);
    slider.addEventListener('mouseleave', onMouseLeave);

    startSliderAutoplay();

    return () => {
      stopSliderAutoplay();
      slider.removeEventListener('mousedown', onMouseDown);
      slider.removeEventListener('click', onClickCapture, true);
      slider.removeEventListener('mouseenter', onMouseEnter);
      slider.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      if (dragRef.animationFrameId !== null) {
        window.cancelAnimationFrame(dragRef.animationFrameId);
        dragRef.animationFrameId = null;
      }

      slider.classList.remove('is-grabbing');
    };
  }, [sliderRef]);

  const renderEditableOrText = (contentKey, fallback) => {
    if (isEditModeActive) {
      return (
        <EditableContent
          contentKey={contentKey}
          language={editryLanguage}
          as="span"
          fallback={getContentValue(allContent, contentKey, fallback, preferredLanguages)}
          showFallbackWhileLoading={false}
        />
      );
    }

    return (
      <span>{getContentValue(allContent, contentKey, fallback, preferredLanguages)}</span>
    );
  };

  const newsItems = useMemo(() => {
    if (!editryReady) {
      return mockNewsArticles;
    }

    const published = publishedNewsResponse.data?.data || [];

    if (!published.length) {
      return [];
    }

    return published.slice(0, 3).map((item) => ({
      data: {
        image: item.image_url || nukeproofImage,
        tag: 'Novinky',
        title: item.title || 'Článek',
        description: item.excerpt || toPlainText(item.content || ''),
        route: item.id,
      },
      linkTo: `/article/${item.id}`,
    }));
  }, [publishedNewsResponse.data, editryReady]);

  const safeCategories = categoryData.length > 0 ? categoryData : mockBikeCategories;
  const safeNews = newsItems.length > 0 ? newsItems : mockNewsArticles;

  const hrefTo = (link) => {
    navigate(link);
  };

  return (
    <>
      <HomeHeroHeader
        scrollTo={scrollTo}
        allContent={allContent}
        preferredLanguages={preferredLanguages}
        isEditModeActive={isEditModeActive}
        editryLanguage={editryLanguage}
      />
      <motion.div key="home-content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div id="kola">
          <div
            className="container2 barlow-bold animated scrollto clearfix pointer"
            id="menu-kola"
            ref={sliderRef}
          >
            {safeCategories.map((pick, index) => (
              <BikeCategory
                key={index}
                pick={pick}
                index={index}
                hrefTo={hrefTo}
                nukeproofImage={nukeproofImage}
              />
            ))}
          </div>
        </div>

        <section className="text-center scrollto clearfix">
          <div className="row clearfix">
            <div className="section-heading">
              <h3 className="barlow-semibold small-font-dark">
                {renderEditableOrText('home_kola_homedata_novinkytitle', newsTitleText)}
              </h3>
              <h2 className="section-title white-title">
                {renderEditableOrText('home_kola_homedata_novinkydesc', 'Aktuální články')}
              </h2>
            </div>

            <div className="novinky-container">
              {safeNews.slice(0, 3).map((novinka, index) => (
                <NovinkyItem
                  key={index}
                  novinka={novinka}
                  index={index}
                  nukeproofImage={nukeproofImage}
                />
              ))}
            </div>

            <button className="articles-more" onClick={() => navigate('/articles')}>
              <span>{moreArticlesText}</span>
            </button>
          </div>
        </section>

        <InstagramPosts />

        <section id="prodejci" className="text-center scrollto clearfix" style={{ marginBottom: '120px' }}>
          <NavLink to="/prodejci">
            <div className="home-bike-dealer">
              <div className="home-bike-prodejci">
                <h1>
                  {renderEditableOrText('home_kola_homedata_homebikeprodejci1', dealersTitleText)}
                </h1>
                <h2>
                  {renderEditableOrText('home_kola_homedata_homebikeprodejci2', dealersSubtitleText)}
                </h2>
                <button>
                  <label>{dealersButtonText}</label>
                </button>
              </div>
              <img src={bikedealer} alt="Nukeproof bike dealers" />
            </div>
          </NavLink>
        </section>
      </motion.div>
    </>
  );
}

export default HomeEditry;
